import {Matrix4} from "@cm/math"
import type {EulerValue} from "#template/nodes/types"
import {blenderToPlatformAxes, platformToBlenderAxes} from "#template/utils/blender-coordinates"

// Rz·Ry·Rx, the order Blender's XYZ Euler and the renderer's environment Mapping both build
function eulerXyzToMatrix([x, y, z]: readonly number[]): Matrix4 {
    return Matrix4.rotationZ(z).multiply(Matrix4.rotationY(y)).multiply(Matrix4.rotationX(x))
}

// angles an exact inverse would give only up to rounding, so a value that went through here reads as it was entered
const tidy = (degrees: number) => {
    const rounded = Math.round(degrees * 1e9) / 1e9
    return rounded === 0 ? 0 : rounded
}

// "ZYX" is Rz·Ry·Rx read back, as to3dsMaxTransform reads Blender's and Max's rotations
function matrixToEulerXyz(matrix: Matrix4): EulerValue {
    const {x, y, z} = matrix.decompose().quaternion.toEuler("ZYX")
    return [tidy(x * (180 / Math.PI)), tidy(y * (180 / Math.PI)), tidy(z * (180 / Math.PI))]
}

/** The rotation of Blender's world Mapping that turns the environment as the platform's rotation does: the renderer
 * turns its Y-up lookup to the image's Z-up one after the environment's own rotation. Mirroring is about X in both,
 * which the axis change leaves alone. */
export function blenderEnvironmentRotation(platformRotation: readonly number[]): EulerValue {
    const [x, y, z] = matrixToEulerXyz(platformToBlenderAxes.multiply(eulerXyzToMatrix(platformRotation)).multiply(blenderToPlatformAxes))
    // the turn about the horizon as the platform's own slider showed it, from 0 to 360
    return [x, y, tidy(((z % 360) + 360) % 360)]
}

export function platformEnvironmentRotation(blenderRotation: readonly number[]): EulerValue {
    return matrixToEulerXyz(blenderToPlatformAxes.multiply(eulerXyzToMatrix(blenderRotation)).multiply(platformToBlenderAxes))
}

export function environmentRotationMatrix(rotation: readonly number[]): number[][] {
    const elements = eulerXyzToMatrix(rotation).toArray()
    return [0, 1, 2].map((row) => [0, 1, 2].map((column) => elements[column * 4 + row]))
}
